const API_BASE = '/api';
const SUPABASE_URL = 'https://oxojfhbehvgpvsdxcbec.supabase.co';

let session = null;
let user = null;
let onAuthChange = null;

export function setAuthChangeHandler(handler) {
  onAuthChange = handler;
}

export function getSession() {
  return session;
}

export function getUser() {
  return user;
}

export function setUser(u) {
  user = u;
  if (u) localStorage.setItem('memo_user', JSON.stringify(u));
}

export function getToken() {
  return session?.access_token || null;
}

export function isLoggedIn() {
  return !!session;
}

// Redirect to Supabase Google OAuth (implicit flow — tokens in hash)
export function startGoogleLogin() {
  const redirectTo = encodeURIComponent(location.origin);
  location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${redirectTo}`;
}

// Handle OAuth callback (session in query param from Worker, or legacy hash)
export async function handleOAuthCallback() {
  let accessToken, refreshToken, expiresIn;

  // New flow: session in query param (from Worker redirect)
  const urlParams = new URLSearchParams(location.search);
  const sessionStr = urlParams.get('_session');
  if (sessionStr) {
    const sp = new URLSearchParams(sessionStr);
    accessToken = sp.get('access_token');
    refreshToken = sp.get('refresh_token');
    expiresIn = parseInt(sp.get('expires_in') || '3600', 10);
    // Clear from URL
    history.replaceState(null, '', location.pathname);
  }

  // Legacy fallback: token in hash (for existing sessions/redirects)
  if (!accessToken) {
    const hash = window.__oauthHash || location.hash;
    delete window.__oauthHash;
    if (!hash || !hash.includes('access_token')) return false;
    const hp = new URLSearchParams(hash.substring(1));
    accessToken = hp.get('access_token');
    refreshToken = hp.get('refresh_token');
    expiresIn = parseInt(hp.get('expires_in') || '3600', 10);
    if (location.hash) history.replaceState(null, '', location.pathname);
  }

  if (!accessToken) return false;

  // Fetch user info from Worker
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!res.ok) throw new Error('Failed to fetch user');

    const data = await res.json();
    user = data.user;
    session = {
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: Date.now() + expiresIn * 1000,
    };

    localStorage.setItem('memo_session', JSON.stringify(session));
    localStorage.setItem('memo_user', JSON.stringify(user));

    scheduleRefresh();
    onAuthChange?.('signed_in', user);
    return true;
  } catch (e) {
    console.error('OAuth callback error:', e);
    return false;
  }
}

let refreshPromise = null;

export async function refreshToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = _doRefresh().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

async function _doRefresh() {
  if (!session?.refresh_token) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });

    if (!res.ok) {
      signOut();
      return false;
    }

    const data = await res.json();
    session = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: Date.now() + data.expires_in * 1000,
    };
    localStorage.setItem('memo_session', JSON.stringify(session));
    scheduleRefresh();
    return true;
  } catch {
    return false;
  }
}

export function signOut() {
  session = null;
  user = null;
  localStorage.removeItem('memo_session');
  localStorage.removeItem('memo_user');
  onAuthChange?.('signed_out', null);
}

let refreshTimer = null;

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  if (!session?.expires_at) return;
  const delay = Math.max(0, session.expires_at - Date.now() - 5 * 60 * 1000);
  refreshTimer = setTimeout(refreshToken, delay);
}

export function restoreSession() {
  try {
    const s = localStorage.getItem('memo_session');
    const u = localStorage.getItem('memo_user');
    if (s && u) {
      session = JSON.parse(s);
      user = JSON.parse(u);
      if (session.expires_at < Date.now()) {
        refreshToken();
      } else {
        scheduleRefresh();
      }
      return true;
    }
  } catch {}
  return false;
}
